import { type ChangeEventHandler, useEffect } from "react";

import { type ProductDetailsProductData } from "@jti/fe-common";
import { useForm } from "@jti/ui/components/Form";
import { useAddProduct } from "@jti/ui/hooks/useAddProduct";

import { errorClient } from "@product-details/clients/ErrorClient";
import type { ConsumableMatrixItem } from "@product-details/containers";
import { useConsumablesProductDetailsContext } from "@product-details/providers/ConsumablesProductDetailsProvider";
import { dispatchViewItemEvent } from "@product-details/utils/dispatchViewItemEvent";
import { getBundleProductCartPayload } from "@product-details/utils/getBundleProductCartPayload";
import { getSingleFlavorBundleProductCartPayload } from "@product-details/utils/getSingleFlavorBundleProductCartPayload";

import { type FlavorSelectorSubmitHandlerPayload } from "../../components/ConsumablesFlavorSelectorSection";
import type { ConsumablesFormValues } from "../../ConsumablesForm.types";
import { findPackagingFormat } from "../../utils/findPackagingFormat";
import { getInitialFormValues } from "../../utils/getInitialFormValues";
import { getMatrixItemByFlavor } from "../../utils/getMatrixItemByFlavor/getMatrixItemByFlavor";
import { getMatrixItemByProperties } from "../../utils/getMatrixItemByProperties/getMatrixItemByProperties";
import { getMatrixItemForNextAvailablePackagingFormat } from "../../utils/getMatrixItemForNextAvailablePackagingFormat";
import { getMatrixItemsByPackagingFormat } from "../../utils/getMatrixItemsByPackagingFormat";
import { getMatrixItemsByPurchaseType } from "../../utils/getMatrixItemsByPurchaseType";
import { getSelectedPackagesCount } from "../../utils/getSelectedPackagesCount";
import { getValidTierPrices } from "../../utils/getValidTierPrices";

export const useConsumablesForm = ({
  isPriceDropdownEnabled = false,
  isSubscriptionsEnabled,
}: {
  isPriceDropdownEnabled?: boolean;
  isSubscriptionsEnabled?: boolean;
} = {}) => {
  const { currentMatrixItem, setCurrentSKU, consumablesProductDetailsComponentData } =
    useConsumablesProductDetailsContext();
  const { consumablesMatrixData } = consumablesProductDetailsComponentData;
  const tierPrices = getValidTierPrices(currentMatrixItem.product.tierPrices);
  const firstTierPriceQuantity = tierPrices[0]?.quantity;
  const initialQuantity = isPriceDropdownEnabled && tierPrices[0] ? String(tierPrices[0].quantity) : "1";

  const form = useForm<ConsumablesFormValues>({
    defaultValues: {
      ...getInitialFormValues(currentMatrixItem.sku, consumablesMatrixData),
      quantity: initialQuantity,
    },
  });

  const { isRunning: isAddProductInProgress, addProductToCart } = useAddProduct(
    currentMatrixItem.sku,
    undefined,
    false,
    () => form.setError("root", { type: "add-product-to-cart" })
  );

  const packagingFormatIdInputValue = form.watch("packagingFormatId");
  const quantityInputValue = form.watch("quantity");
  const purchaseTypeValue = form.watch("purchaseType");

  const dispatchNewMatrixItemViewItemEvent = (nextMatrixItem: ConsumableMatrixItem) => {
    dispatchViewItemEvent({
      productName: nextMatrixItem.product.name,
      sku: nextMatrixItem.sku,
      color: nextMatrixItem.product.color ?? null,
      flavor: nextMatrixItem?.product?.flavor,
      categories: nextMatrixItem.product.categories ?? null,
      discount: nextMatrixItem.product?.tierPrices?.[0]?.discount?.percentOff ?? 0,
      priceInformation: nextMatrixItem.product?.tierPrices?.[0] ?? null,
    });
  };

  const onPurchaseTypeChange = (newPurchaseType: ConsumablesFormValues["purchaseType"]): boolean => {
    form.clearErrors();

    const matrixItemsWithNewPurchaseType = getMatrixItemsByPurchaseType(consumablesMatrixData, newPurchaseType);

    if (!matrixItemsWithNewPurchaseType.length) {
      form.setError("root", {
        type: "custom",
        message: `There are no products for ${newPurchaseType}. Please, choose products for ${purchaseTypeValue}`,
      });

      return false;
    }

    const matrixItemsWithSamePackagingFormat = getMatrixItemsByPackagingFormat(
      matrixItemsWithNewPurchaseType,
      currentMatrixItem.packagingFormatId
    );

    if (!matrixItemsWithSamePackagingFormat.length) {
      const nextMatrixItem = getMatrixItemForNextAvailablePackagingFormat(
        consumablesMatrixData,
        packagingFormatIdInputValue,
        newPurchaseType,
        currentMatrixItem.flavorDetails?.flavour
      );

      if (!nextMatrixItem) {
        form.setError("root", {
          type: "matrixMisconfiguration",
          message: `There are no products for ${newPurchaseType}. Please, choose products for ${purchaseTypeValue}`,
        });
        errorClient.reportError(
          `No matrix item found for the next available packaging format for purchase type: ${newPurchaseType} and packaging format: ${packagingFormatIdInputValue}`
        );
        return false;
      }

      dispatchNewMatrixItemViewItemEvent(nextMatrixItem);
      setCurrentSKU(nextMatrixItem.sku);
      return true;
    }

    const nextMatrixItemWithSameFlavor = getMatrixItemByFlavor(
      matrixItemsWithSamePackagingFormat,
      currentMatrixItem.flavorDetails?.flavour
    );
    const nextMatrixItem = nextMatrixItemWithSameFlavor ?? matrixItemsWithSamePackagingFormat[0];

    dispatchNewMatrixItemViewItemEvent(nextMatrixItem);
    setCurrentSKU(nextMatrixItem.sku);
    return true;
  };

  const onPackagingFormatChange = (newPackagingFormatId: ConsumablesFormValues["packagingFormatId"]) => {
    form.clearErrors();

    const newPackagingFormat = findPackagingFormat(consumablesMatrixData, newPackagingFormatId);

    if (!newPackagingFormat) {
      errorClient.reportError(`No packaging format found for the selected packaging option: ${newPackagingFormatId}`);
      return false;
    }

    const purchaseType = newPackagingFormat.contentsType === "pack" ? "regular" : currentMatrixItem.purchaseType;

    const itemsWithNewPackagingFormat = getMatrixItemsByPackagingFormat(
      consumablesMatrixData.items,
      newPackagingFormatId
    );

    let itemsWithSamePurchaseType = itemsWithNewPackagingFormat.filter((item) => item.purchaseType === purchaseType);

    if (!itemsWithSamePurchaseType.length) {
      const switchedPurchaseType = purchaseType === "regular" && isSubscriptionsEnabled ? "subscription" : "regular";
      itemsWithSamePurchaseType = itemsWithNewPackagingFormat.filter(
        (item) => item.purchaseType === switchedPurchaseType
      );
    }

    const itemWithSameFlavor = itemsWithSamePurchaseType.find(
      (item) => item.flavorDetails?.flavour === currentMatrixItem.flavorDetails?.flavour
    );

    const nextMatrixItem = itemWithSameFlavor ?? itemsWithSamePurchaseType[0];

    if (!nextMatrixItem) {
      errorClient.reportError(
        `No SKU found for the selected packaging option in any available purchase type: ${newPackagingFormatId}`
      );
      return false;
    }

    if (itemWithSameFlavor) {
      dispatchNewMatrixItemViewItemEvent(nextMatrixItem);
    }

    setCurrentSKU(nextMatrixItem.sku);
    return true;
  };

  const onFlavorsChange = (payload: FlavorSelectorSubmitHandlerPayload) => {
    form.clearErrors();
    if ("sku" in payload) {
      setCurrentSKU(payload.sku);
      return;
    }

    form.setValue("flavors", payload.flavorsSelection);
  };

  const onStrengthChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newRating = Number(e.target.value);
    form.clearErrors();

    const nextMatrixItem = getMatrixItemByProperties(
      consumablesMatrixData.items,
      currentMatrixItem.packagingFormatId,
      currentMatrixItem.purchaseType,
      currentMatrixItem.flavorDetails?.flavour,
      newRating
    );

    if (!nextMatrixItem) {
      errorClient.reportError(`No SKU found for strength rating: ${newRating}`);
      return;
    }

    dispatchNewMatrixItemViewItemEvent(nextMatrixItem);
    setCurrentSKU(nextMatrixItem.sku);
  };

  const onQuantityChange = (quantity: string) => {
    form.setValue("quantity", quantity);
  };

  const getBundleConfiguration = (product: ProductDetailsProductData | undefined) => {
    const bundleConfiguration = product?.productBundleConfiguration?.items?.[0];

    if (!bundleConfiguration) {
      const errorMessage = "Product misconfiguration. No options available for single flavor bundle product.";
      errorClient.reportError(errorMessage);

      throw new Error(errorMessage);
    }

    return bundleConfiguration;
  };

  const handleMixedFlavorBundleMisconfiguration = (flavors: ConsumablesFormValues["flavors"]) => {
    form.setError("root", {
      type: "mixedBundleFlavorsMisconfiguration",
    });
    errorClient.reportError(`No bundle configuration found for the selected flavors: ${JSON.stringify(flavors)}`);
  };

  const addMixedFlavorBundleToCart = (data: ConsumablesFormValues) => {
    const bundleConfiguration = getBundleConfiguration(currentMatrixItem.product);

    if (!bundleConfiguration) {
      form.setError("root", {
        type: "bundleMisconfiguration",
      });
      return;
    }

    if (!data.flavors) {
      handleMixedFlavorBundleMisconfiguration(data.flavors);
      return;
    }

    const selectedPackageCount = getSelectedPackagesCount(data.flavors);

    if (selectedPackageCount !== data.packagingFormat?.packagesPerBundle) {
      handleMixedFlavorBundleMisconfiguration(data.flavors);
      return;
    }

    const payload = getBundleProductCartPayload(bundleConfiguration.optionId, data.flavors);

    addProductToCart(+quantityInputValue, payload);
  };

  const addSingleFlavorBundleToCart = (quantity: string, product: ProductDetailsProductData | undefined) => {
    const bundleConfiguration = getBundleConfiguration(product);
    const payload = getSingleFlavorBundleProductCartPayload(bundleConfiguration);

    if (!payload) {
      errorClient.reportError("Something's wrong with selected bundle config");

      return;
    }

    addProductToCart(+quantity, payload);
  };

  const handleSubmit = (data: ConsumablesFormValues) => {
    form.clearErrors();
    const packagingFormat = data.packagingFormat;

    if (!packagingFormat) {
      errorClient.reportError(
        `No packaging format found for the selected packaging option: ${packagingFormatIdInputValue}`
      );
      return;
    }

    if (packagingFormat.contentsType === "pack") {
      addProductToCart(+data.quantity);
      return;
    }

    if (packagingFormat.contentsType === "mixed") {
      addMixedFlavorBundleToCart(data);
      return;
    }

    addSingleFlavorBundleToCart(data.quantity, currentMatrixItem.product);
  };

  useEffect(() => {
    form.setValue("purchaseType", currentMatrixItem.purchaseType);
    form.setValue("packagingFormatId", currentMatrixItem.packagingFormatId);
    form.setValue(
      "strengthSelector",
      currentMatrixItem.strengthDetails?.rating ? String(currentMatrixItem.strengthDetails.rating) : undefined
    );

    const newPackagingFormat = findPackagingFormat(consumablesMatrixData, currentMatrixItem.packagingFormatId);

    if (newPackagingFormat) {
      form.setValue("packagingFormat", newPackagingFormat);
    }
  }, [currentMatrixItem]);

  useEffect(() => {
    const quantity = isPriceDropdownEnabled && firstTierPriceQuantity ? String(firstTierPriceQuantity) : "1";

    form.setValue("quantity", quantity);
  }, [currentMatrixItem.sku, firstTierPriceQuantity, form, isPriceDropdownEnabled, packagingFormatIdInputValue]);

  return {
    form,
    onPurchaseTypeChange,
    onPackagingFormatChange,
    onFlavorsChange,
    onStrengthChange,
    onQuantityChange,
    handleSubmit,
    isSubmitting: isAddProductInProgress,
    tierPrices,
  };
};
